/**
 * Store Import Service
 * 
 * Provides functionality to parse CSV and XLSX files containing store data,
 * validate the data, and import stores into Firestore.
 * 
 * @module StoreImportService
 */

import { Timestamp, writeBatch, collection, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import * as XLSX from 'xlsx';

/**
 * CSV Store Row Interface
 * Represents a single store row from the CSV file
 */
export interface CSVStoreRow {
  CustomerNo: string;
  SalespersonNo?: string;
  SalesGroup?: string;
  StoreName: string;
  Address: string;
  City: string;
  Prov?: string;
  PostalCode?: string;
  StoreNumber?: string;
  SortField?: string;
  Telephone?: string;
}

/**
 * Store Import Row
 * Extended interface with validation status
 */
export interface StoreImportRow extends CSVStoreRow {
  id?: string; // Temporary ID for table editing
  errors?: string[]; // Validation errors for this row
  isValid?: boolean; // Whether this row is valid
}

/**
 * Parse Result
 * Result of CSV parsing operation
 */
export interface StoreParseResult {
  stores: StoreImportRow[];
  errors: ParseError[];
  metadata: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
  };
}

/**
 * Parse Error
 * Error information for a specific row
 */
export interface ParseError {
  row: number;
  field?: string;
  message: string;
  data?: string;
}

/**
 * Import Result
 * Result of store import operation
 */
export interface ImportResult {
  success: boolean;
  importedCount: number;
  failedCount: number;
  errors: string[];
  message?: string;
}

/**
 * Store Import Service
 * Handles parsing, validation, and import of store CSV files
 */
export class StoreImportService {
  private static readonly REQUIRED_COLUMNS = ['CustomerNo', 'StoreName', 'Address', 'City'];
  private static readonly ALL_COLUMNS = [
    'CustomerNo',
    'SalespersonNo',
    'SalesGroup',
    'StoreName',
    'Address',
    'City',
    'Prov',
    'PostalCode',
    'StoreNumber',
    'SortField',
    'Telephone'
  ];
  private static readonly ACCESS_CODE = 'MCKENZIE_IMPORT';

  /**
   * Validate access code
   * 
   * @param code - Access code to validate
   * @returns True if code is correct
   */
  static validateAccessCode(code: string): boolean {
    return code.trim() === this.ACCESS_CODE;
  }

  /**
   * Parse a store file (CSV or XLSX)
   * 
   * @param file - CSV or XLSX file to parse
   * @returns Promise resolving to parse result
   */
  static async parseStoreFile(file: File): Promise<StoreParseResult> {
    // Check file extension to determine format
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      return this.parseStoreXLSX(file);
    } else {
      return this.parseStoreCSV(file);
    }
  }

  /**
   * Parse a store XLSX file
   * 
   * @param file - XLSX file to parse
   * @returns Promise resolving to parse result
   */
  static async parseStoreXLSX(file: File): Promise<StoreParseResult> {
    const errors: ParseError[] = [];
    const stores: StoreImportRow[] = [];

    try {
      console.log('[StoreImportService] Starting XLSX file parse:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Read file as array buffer
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      
      // Parse workbook
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get first sheet
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        throw new Error('Excel file must contain at least one sheet');
      }

      const worksheet = workbook.Sheets[firstSheetName];
      
      // Convert to JSON (header row will be first row)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        defval: '', // Default value for empty cells
        raw: false // Convert all values to strings
      }) as any[][];

      if (jsonData.length < 2) {
        throw new Error('File must contain at least a header row and one data row');
      }

      // Get headers from first row
      const headers = (jsonData[0] || []).map((h: any) => String(h || '').trim()).filter((h: string) => h.length > 0);
      console.log('[StoreImportService] Headers found:', headers);

      // Normalize headers
      const normalizedHeaders = headers.map(h => this.normalizeHeader(h));
      
      // Validate required headers
      const missingHeaders = this.REQUIRED_COLUMNS.filter(
        req => !normalizedHeaders.includes(req)
      );
      
      if (missingHeaders.length > 0) {
        errors.push({
          row: 1,
          field: 'headers',
          message: `Missing required columns: ${missingHeaders.join(', ')}`
        });
      }

      // Parse data rows
      for (let i = 1; i < jsonData.length; i++) {
        const lineNumber = i + 1; // 1-indexed for user display
        const row = jsonData[i];

        try {
          // Skip empty rows
          if (!row || row.every((cell: any) => !cell || String(cell).trim() === '')) {
            continue;
          }

          // Create store row object
          const store: any = {};
          
          headers.forEach((header, index) => {
            const value = row[index];
            const normalizedHeader = this.normalizeHeader(header.trim());
            store[normalizedHeader] = value ? String(value).trim() : '';
          });

          // Validate row
          const validationErrors = this.validateStoreRow(store, lineNumber);
          
          const storeRow: StoreImportRow = {
            ...store,
            id: `temp-${i}`,
            errors: validationErrors.map(e => e.message),
            isValid: validationErrors.length === 0
          };

          stores.push(storeRow);

          if (validationErrors.length > 0) {
            errors.push(...validationErrors);
          }
        } catch (error: any) {
          errors.push({
            row: lineNumber,
            message: `Error parsing row: ${error.message}`,
            data: JSON.stringify(row)
          });
        }
      }

      const validRows = stores.filter(s => s.isValid).length;
      const invalidRows = stores.length - validRows;

      console.log('[StoreImportService] XLSX parse complete:', {
        totalRows: stores.length,
        validRows,
        invalidRows
      });

      return {
        stores,
        errors,
        metadata: {
          totalRows: stores.length,
          validRows,
          invalidRows
        }
      };
    } catch (error: any) {
      console.error('[StoreImportService] Fatal error parsing XLSX file:', error);
      errors.push({
        row: 0,
        field: 'file',
        message: `Fatal error: ${error.message}`
      });

      return {
        stores: [],
        errors,
        metadata: {
          totalRows: 0,
          validRows: 0,
          invalidRows: errors.length
        }
      };
    }
  }

  /**
   * Parse a store CSV file
   * 
   * @param file - CSV file to parse
   * @returns Promise resolving to parse result
   */
  static async parseStoreCSV(file: File): Promise<StoreParseResult> {
    const errors: ParseError[] = [];
    const stores: StoreImportRow[] = [];

    try {
      console.log('[StoreImportService] Starting file parse:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Read file content
      const text = await this.readFileAsText(file);
      
      // Split into lines
      const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
      
      if (lines.length < 2) {
        throw new Error('File must contain at least a header row and one data row');
      }

      // Detect delimiter (comma or semicolon)
      const delimiter = this.detectDelimiter(lines[0]);
      console.log('[StoreImportService] Detected delimiter:', delimiter === ',' ? 'comma' : 'semicolon');

      // Parse header row
      const headers = this.parseCSVLine(lines[0], delimiter);
      console.log('[StoreImportService] Headers found:', headers);

      // Normalize headers (trim and handle case variations)
      const normalizedHeaders = headers.map(h => this.normalizeHeader(h));
      
      // Validate required headers
      const missingHeaders = this.REQUIRED_COLUMNS.filter(
        req => !normalizedHeaders.includes(req)
      );
      
      if (missingHeaders.length > 0) {
        errors.push({
          row: 1,
          field: 'headers',
          message: `Missing required columns: ${missingHeaders.join(', ')}`
        });
        // Continue parsing even with missing headers - user can fix in table
      }

      // Parse data rows
      for (let i = 1; i < lines.length; i++) {
        const lineNumber = i + 1; // 1-indexed for user display
        const line = lines[i];

        try {
          // Skip empty lines
          if (!line.trim()) {
            continue;
          }

          const values = this.parseCSVLine(line, delimiter);
          
          // Create store row object
          const store: any = {};
          
          headers.forEach((header, index) => {
            const value = values[index] || '';
            const normalizedHeader = this.normalizeHeader(header.trim());
            store[normalizedHeader] = value.trim();
          });

          // Validate row
          const validationErrors = this.validateStoreRow(store, lineNumber);
          
          const storeRow: StoreImportRow = {
            ...store,
            id: `temp-${i}`,
            errors: validationErrors.map(e => e.message),
            isValid: validationErrors.length === 0
          };

          stores.push(storeRow);

          if (validationErrors.length > 0) {
            errors.push(...validationErrors);
          }
        } catch (error: any) {
          errors.push({
            row: lineNumber,
            message: `Error parsing row: ${error.message}`,
            data: line
          });
        }
      }

      const validRows = stores.filter(s => s.isValid).length;
      const invalidRows = stores.length - validRows;

      console.log('[StoreImportService] Parse complete:', {
        totalRows: stores.length,
        validRows,
        invalidRows
      });

      return {
        stores,
        errors,
        metadata: {
          totalRows: stores.length,
          validRows,
          invalidRows
        }
      };
    } catch (error: any) {
      console.error('[StoreImportService] Fatal error parsing file:', error);
      errors.push({
        row: 0,
        field: 'file',
        message: `Fatal error: ${error.message}`
      });

      return {
        stores: [],
        errors,
        metadata: {
          totalRows: 0,
          validRows: 0,
          invalidRows: errors.length
        }
      };
    }
  }

  /**
   * Validate a store row
   * 
   * @param store - Store row to validate
   * @param rowNumber - Row number for error reporting
   * @returns Array of validation errors
   */
  private static validateStoreRow(store: any, rowNumber: number): ParseError[] {
    const errors: ParseError[] = [];

    // Check required fields
    if (!store.CustomerNo || !store.CustomerNo.trim()) {
      errors.push({
        row: rowNumber,
        field: 'CustomerNo',
        message: 'CustomerNo is required'
      });
    }

    if (!store.StoreName || !store.StoreName.trim()) {
      errors.push({
        row: rowNumber,
        field: 'StoreName',
        message: 'StoreName is required'
      });
    }

    if (!store.Address || !store.Address.trim()) {
      errors.push({
        row: rowNumber,
        field: 'Address',
        message: 'Address is required'
      });
    }

    if (!store.City || !store.City.trim()) {
      errors.push({
        row: rowNumber,
        field: 'City',
        message: 'City is required'
      });
    }

    return errors;
  }

  /**
   * Normalize header name (handle case variations and spaces)
   * 
   * @param header - Header name to normalize
   * @returns Normalized header name
   */
  private static normalizeHeader(header: string): string {
    // Remove spaces and convert to PascalCase
    const normalized = header.trim().replace(/\s+/g, '');
    
    // Check if it matches any of our expected columns (case-insensitive)
    for (const col of this.ALL_COLUMNS) {
      if (normalized.toLowerCase() === col.toLowerCase()) {
        return col;
      }
    }
    
    return normalized;
  }

  /**
   * Read file as text with encoding detection
   * 
   * @param file - File to read
   * @returns Promise resolving to file text content
   */
  private static async readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Failed to read file as text'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file, 'UTF-8');
    });
  }

  /**
   * Read file as array buffer (for XLSX files)
   * 
   * @param file - File to read
   * @returns Promise resolving to array buffer
   */
  private static async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const result = e.target?.result;
        if (result instanceof ArrayBuffer) {
          resolve(result);
        } else {
          reject(new Error('Failed to read file as array buffer'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Detect CSV delimiter (comma or semicolon)
   * Checks which delimiter appears more frequently in the first line
   * 
   * @param line - First line of CSV (header row)
   * @returns Detected delimiter (',' or ';')
   */
  private static detectDelimiter(line: string): ',' | ';' {
    // Count occurrences of comma and semicolon (outside quotes)
    let commaCount = 0;
    let semicolonCount = 0;
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (!inQuotes) {
        if (char === ',') commaCount++;
        if (char === ';') semicolonCount++;
      }
    }
    
    // Return the delimiter with more occurrences, default to comma if equal
    return semicolonCount > commaCount ? ';' : ',';
  }

  /**
   * Parse a CSV line (comma or semicolon-delimited)
   * Handles quoted values and escaped quotes
   * 
   * @param line - CSV line to parse
   * @param delimiter - Delimiter to use (',' or ';')
   * @returns Array of field values
   */
  private static parseCSVLine(line: string, delimiter: ',' | ';' = ','): string[] {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        // Field separator
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    values.push(current.trim());
    
    return values;
  }

  /**
   * Import stores to Firestore
   * 
   * @param stores - Array of store rows to import
   * @param userId - ID of the user performing the import
   * @returns Promise resolving to import result
   */
  static async importStores(
    stores: StoreImportRow[],
    userId: string
  ): Promise<ImportResult> {
    const errors: string[] = [];
    let importedCount = 0;
    let failedCount = 0;

    try {
      // Filter to only valid stores
      const validStores = stores.filter(s => s.isValid);

      if (validStores.length === 0) {
        return {
          success: false,
          importedCount: 0,
          failedCount: stores.length,
          errors: ['No valid stores to import'],
          message: 'No valid stores to import. Please fix validation errors and try again.'
        };
      }

      // Use batch writes for efficiency (Firestore limit is 500 per batch)
      const batchSize = 500;
      const batches: StoreImportRow[][] = [];

      for (let i = 0; i < validStores.length; i += batchSize) {
        batches.push(validStores.slice(i, i + batchSize));
      }

      // Process each batch
      for (const batch of batches) {
        const firestoreBatch = writeBatch(db);
        const storesRef = collection(db, 'Stores');

        for (const store of batch) {
          try {
            // Map CSV data to database format
            const dbData: any = {
              CustomerNo: store.CustomerNo,
              StoreName: store.StoreName,
              Address: store.Address,
              City: store.City,
              Status: 'pending',
              CreatedAt: Timestamp.now(),
              LastUpdated: Timestamp.now(),
              CreatedBy: userId,
              UpdatedBy: userId,
              TotalOrders: 0,
              TotalRevenue: 0
            };

            // Add optional fields
            if (store.Prov) dbData.Prov = store.Prov;
            if (store.PostalCode) dbData.PostalCode = store.PostalCode;
            if (store.StoreNumber) dbData.StoreNumber = store.StoreNumber;
            if (store.SortField) dbData.SortField = store.SortField;
            if (store.Telephone) dbData.Telephone = store.Telephone;
            if (store.SalesGroup) dbData.SalesGroup = store.SalesGroup;
            if (store.SalespersonNo) dbData.SalespersonNo = store.SalespersonNo;
            // Note: Not assigning stores to sales reps (as per requirements)

            // Add document to batch
            const docRef = doc(storesRef);
            firestoreBatch.set(docRef, dbData);
          } catch (error: any) {
            failedCount++;
            errors.push(`Failed to prepare store ${store.StoreName}: ${error.message}`);
          }
        }

        // Commit batch
        try {
          await firestoreBatch.commit();
          importedCount += batch.length;
        } catch (error: any) {
          failedCount += batch.length;
          errors.push(`Failed to import batch: ${error.message}`);
        }
      }

      return {
        success: importedCount > 0,
        importedCount,
        failedCount,
        errors: errors.length > 0 ? errors : undefined,
        message: `Successfully imported ${importedCount} store(s)${failedCount > 0 ? `. ${failedCount} store(s) failed.` : ''}`
      };
    } catch (error: any) {
      console.error('[StoreImportService] Error importing stores:', error);
      return {
        success: false,
        importedCount,
        failedCount: stores.length - importedCount,
        errors: [error.message || 'Failed to import stores'],
        message: 'Failed to import stores. Please try again.'
      };
    }
  }

  /**
   * Generate CSV template
   * 
   * @returns CSV template string (comma-delimited for better compatibility)
   */
  static generateCSVTemplate(): string {
    const headers = this.ALL_COLUMNS.join(',');
    const sampleRow1 = '12345,SP001,SG01,Sample Store 1,123 Main St,Toronto,ON,M5H 2N2,ST001,SF001,416-555-0100';
    const sampleRow2 = '12346,SP002,SG02,Sample Store 2,456 Oak Ave,Vancouver,BC,V6B 1A1,ST002,SF002,604-555-0200';
    
    return `${headers}\n${sampleRow1}\n${sampleRow2}`;
  }

  /**
   * Download CSV template
   */
  static downloadCSVTemplate(): void {
    const template = this.generateCSVTemplate();
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'store_import_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }
}

